/**
 * Machine-readable error codes.
 *
 * The UI switches on `code`, never on `message`. That means we can reword any
 * user-facing message without breaking client logic, and translate messages
 * later without touching a single conditional.
 */
export const ERROR_CODES = {
  // 400 / 422 — the request itself is wrong
  VALIDATION_FAILED: 'VALIDATION_FAILED',
  MALFORMED_JSON: 'MALFORMED_JSON',
  INVALID_CURSOR: 'INVALID_CURSOR',

  // 401 — we do not know who you are
  UNAUTHENTICATED: 'UNAUTHENTICATED',
  SESSION_EXPIRED: 'SESSION_EXPIRED',
  INVALID_CREDENTIALS: 'INVALID_CREDENTIALS',
  INVALID_TOKEN: 'INVALID_TOKEN',

  // 403 — we know who you are, and you may not do this
  FORBIDDEN: 'FORBIDDEN',
  INSUFFICIENT_ROLE: 'INSUFFICIENT_ROLE',
  NOT_BOARD_MEMBER: 'NOT_BOARD_MEMBER',
  ACCOUNT_SUSPENDED: 'ACCOUNT_SUSPENDED',
  ACCOUNT_NOT_PROVISIONED: 'ACCOUNT_NOT_PROVISIONED',
  EMAIL_DOMAIN_NOT_ALLOWED: 'EMAIL_DOMAIN_NOT_ALLOWED',
  EMAIL_NOT_VERIFIED: 'EMAIL_NOT_VERIFIED',
  CSRF_FAILED: 'CSRF_FAILED',
  LAST_SUPER_ADMIN: 'LAST_SUPER_ADMIN',

  // 404
  NOT_FOUND: 'NOT_FOUND',
  BOARD_NOT_FOUND: 'BOARD_NOT_FOUND',
  LIST_NOT_FOUND: 'LIST_NOT_FOUND',
  CARD_NOT_FOUND: 'CARD_NOT_FOUND',
  USER_NOT_FOUND: 'USER_NOT_FOUND',

  // 409
  CONFLICT: 'CONFLICT',
  STALE_RESOURCE: 'STALE_RESOURCE',
  EMAIL_ALREADY_EXISTS: 'EMAIL_ALREADY_EXISTS',

  // 413 / 415
  PAYLOAD_TOO_LARGE: 'PAYLOAD_TOO_LARGE',
  UNSUPPORTED_FILE_TYPE: 'UNSUPPORTED_FILE_TYPE',
  STORAGE_QUOTA_EXCEEDED: 'STORAGE_QUOTA_EXCEEDED',

  // 429
  RATE_LIMITED: 'RATE_LIMITED',
  ACCOUNT_LOCKED: 'ACCOUNT_LOCKED',

  // 500 / 503
  INTERNAL_ERROR: 'INTERNAL_ERROR',
  SERVICE_UNAVAILABLE: 'SERVICE_UNAVAILABLE',
} as const;

export type ErrorCode = (typeof ERROR_CODES)[keyof typeof ERROR_CODES];

/** Default HTTP status for each code. Handlers may override where warranted. */
export const ERROR_STATUS: Record<ErrorCode, number> = {
  VALIDATION_FAILED: 400,
  MALFORMED_JSON: 400,
  INVALID_CURSOR: 400,

  UNAUTHENTICATED: 401,
  SESSION_EXPIRED: 401,
  INVALID_CREDENTIALS: 401,
  INVALID_TOKEN: 401,

  FORBIDDEN: 403,
  INSUFFICIENT_ROLE: 403,
  NOT_BOARD_MEMBER: 403,
  ACCOUNT_SUSPENDED: 403,
  ACCOUNT_NOT_PROVISIONED: 403,
  EMAIL_DOMAIN_NOT_ALLOWED: 403,
  EMAIL_NOT_VERIFIED: 403,
  CSRF_FAILED: 403,
  LAST_SUPER_ADMIN: 403,

  NOT_FOUND: 404,
  BOARD_NOT_FOUND: 404,
  LIST_NOT_FOUND: 404,
  CARD_NOT_FOUND: 404,
  USER_NOT_FOUND: 404,

  CONFLICT: 409,
  STALE_RESOURCE: 409,
  EMAIL_ALREADY_EXISTS: 409,

  PAYLOAD_TOO_LARGE: 413,
  UNSUPPORTED_FILE_TYPE: 415,
  STORAGE_QUOTA_EXCEEDED: 413,

  RATE_LIMITED: 429,
  ACCOUNT_LOCKED: 429,

  INTERNAL_ERROR: 500,
  SERVICE_UNAVAILABLE: 503,
};

/**
 * Safe, user-facing default messages.
 *
 * Note the deliberate vagueness on auth failures: we never reveal whether an
 * email exists, because that turns a login form into an account-enumeration
 * oracle.
 */
export const ERROR_MESSAGES: Record<ErrorCode, string> = {
  VALIDATION_FAILED: 'Some of the information provided is not valid.',
  MALFORMED_JSON: 'The request body could not be read.',
  INVALID_CURSOR: 'That page reference is no longer valid. Please reload.',

  UNAUTHENTICATED: 'Please sign in to continue.',
  SESSION_EXPIRED: 'Your session has expired. Please sign in again.',
  INVALID_CREDENTIALS: 'That email or password is incorrect.',
  INVALID_TOKEN: 'This link is invalid or has expired.',

  FORBIDDEN: 'You do not have permission to do that.',
  INSUFFICIENT_ROLE: 'Your role does not allow this action.',
  NOT_BOARD_MEMBER: 'You do not have access to this board.',
  ACCOUNT_SUSPENDED: 'This account has been suspended. Please contact an administrator.',
  ACCOUNT_NOT_PROVISIONED:
    'No TaskFlow account exists for this address. Ask an administrator for access.',
  EMAIL_DOMAIN_NOT_ALLOWED: 'This email domain is not permitted to sign in.',
  EMAIL_NOT_VERIFIED: 'Your email address must be verified before signing in.',
  CSRF_FAILED: 'The request could not be verified. Please reload and try again.',
  LAST_SUPER_ADMIN: 'The last Super Admin cannot be removed or demoted.',

  NOT_FOUND: 'That item could not be found.',
  BOARD_NOT_FOUND: 'That board could not be found.',
  LIST_NOT_FOUND: 'That list could not be found.',
  CARD_NOT_FOUND: 'That card could not be found.',
  USER_NOT_FOUND: 'That user could not be found.',

  CONFLICT: 'That action conflicts with the current state.',
  STALE_RESOURCE: 'Someone else changed this while you were editing. Please review and retry.',
  EMAIL_ALREADY_EXISTS: 'An account with this email already exists.',

  PAYLOAD_TOO_LARGE: 'That file is too large.',
  UNSUPPORTED_FILE_TYPE: 'That file type is not allowed.',
  STORAGE_QUOTA_EXCEEDED: 'The storage limit has been reached. Please remove some attachments.',

  RATE_LIMITED: 'Too many requests. Please wait a moment and try again.',
  ACCOUNT_LOCKED: 'Too many failed attempts. This account is locked for 15 minutes.',

  INTERNAL_ERROR: 'Something went wrong on our side. Please try again.',
  SERVICE_UNAVAILABLE: 'The service is temporarily unavailable. Please try again shortly.',
};

export interface FieldIssue {
  path: string;
  message: string;
}

/**
 * The only error type application code should throw.
 *
 * `expose` distinguishes errors safe to show a user from internal faults whose
 * details must stay in the logs.
 */
export class AppError extends Error {
  readonly code: ErrorCode;
  readonly status: number;
  readonly issues: FieldIssue[] | undefined;
  readonly expose: boolean;
  /** Narrows `Error.cause` so the original failure can be logged without loss. */
  override readonly cause: unknown;

  constructor(
    code: ErrorCode,
    options: { message?: string; issues?: FieldIssue[]; status?: number; cause?: unknown } = {},
  ) {
    super(options.message ?? ERROR_MESSAGES[code]);
    this.name = 'AppError';
    this.code = code;
    this.status = options.status ?? ERROR_STATUS[code];
    this.issues = options.issues;
    this.expose = this.status < 500;
    this.cause = options.cause;
  }

  static isAppError(value: unknown): value is AppError {
    return value instanceof AppError;
  }
}
